import React, { useState, useEffect } from 'react';
import ChatModule from './Chat/ChatModule';
import QuickChat from './Chat/QuickChat';
import './ChatTest.css';

/**
 * ChatTest Component
 * Standalone test page for the Property Chat Module
 *
 * This component allows testing the chat with existing orders from MongoDB (Workflow)
 * or quick testing with individual AI models (Quick Chat)
 */
function ChatTest({ onBack }) {
  const [activeTab, setActiveTab] = useState('quickchat'); // 'workflow' or 'quickchat'
  const [orderId, setOrderId] = useState('');
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chatStarted, setChatStarted] = useState(false);

  // Fetch recent orders on mount
  useEffect(() => {
    fetchRecentOrders();
  }, []);

  const fetchRecentOrders = async () => {
    try {
      setLoading(true);
      // This would need to be implemented on the backend
      // For now, we'll use a placeholder
      setLoading(false);

      // Mock data for testing - replace with actual API call
      setRecentOrders([
        {
          internalId: 'test-order-1',
          propertyAddress: '123 Main St, Grand Rapids, MI',
          createdAt: '2026-01-21T10:00:00Z',
          status: 'draft'
        },
        {
          internalId: 'test-order-2',
          propertyAddress: '456 Oak Ave, Grand Rapids, MI',
          createdAt: '2026-01-21T09:00:00Z',
          status: 'submitted'
        }
      ]);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
      setError('Failed to load recent orders');
      setLoading(false);
    }
  };

  const handleStartChat = () => {
    if (orderId.trim()) {
      setChatStarted(true);
    }
  };

  const handleSelectOrder = (internalId) => {
    setOrderId(internalId);
  };

  const handleReset = () => {
    setChatStarted(false);
    setOrderId('');
    setError(null);
  };

  // Render Quick Chat tab
  if (activeTab === 'quickchat') {
    return (
      <div className="chat-test-container">
        <div className="chat-test-content">
          <QuickChat
            orderId={orderId || null}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
        </div>
      </div>
    );
  }

  // Render Workflow tab with property chat
  if (chatStarted && orderId) {
    return (
      <div className="chat-test-container">
        <div className="chat-test-content">
          <ChatModule
            internalId={orderId}
            userId="test-user"
            showModelResponses={true}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onReset={handleReset}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="chat-test-container">
      <div className="chat-test-setup">
        <div className="setup-header">
          <h1>🧪 Property Chat Test</h1>
          <p className="setup-subtitle">
            Test the multi-AI property chat with existing orders from MongoDB
          </p>
        </div>

        {error && (
          <div className="error-banner">
            ⚠️ {error}
          </div>
        )}

        <div className="setup-section">
          <h2>Option 1: Enter Order ID Manually</h2>
          <div className="order-id-input-group">
            <input
              type="text"
              className="order-id-input"
              placeholder="Enter order internal ID (UUID)"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleStartChat()}
            />
            <button
              className="start-chat-button"
              onClick={handleStartChat}
              disabled={!orderId.trim()}
            >
              Start Chat
            </button>
          </div>
          <p className="help-text">
            Enter the <code>internalId</code> from a MongoDB order document
          </p>
        </div>

        <div className="setup-divider">
          <span>OR</span>
        </div>

        <div className="setup-section">
          <h2>Option 2: Select Recent Order</h2>
          {loading ? (
            <div className="loading-orders">
              <div className="spinner"></div>
              <p>Loading recent orders...</p>
            </div>
          ) : recentOrders.length > 0 ? (
            <div className="recent-orders-list">
              {recentOrders.map((order) => (
                <div
                  key={order.internalId}
                  className={`order-card ${orderId === order.internalId ? 'selected' : ''}`}
                  onClick={() => handleSelectOrder(order.internalId)}
                >
                  <div className="order-card-header">
                    <span className="order-address">{order.propertyAddress}</span>
                    <span className={`order-status status-${order.status}`}>
                      {order.status}
                    </span>
                  </div>
                  <div className="order-card-meta">
                    <code className="order-card-id">{order.internalId}</code>
                    <span className="order-card-date">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-orders">
              <p>No recent orders found</p>
              <p className="help-text">
                Create an order using the LMCU Refinance workflow first
              </p>
            </div>
          )}
        </div>

        <div className="setup-section">
          <h2>How to Get an Order ID</h2>
          <div className="instructions">
            <ol>
              <li>
                <strong>Upload a document</strong> using the "LMCU Refinance" workflow
              </li>
              <li>
                <strong>Complete the extraction</strong> - the system will save the order to MongoDB
              </li>
              <li>
                <strong>Find the internal ID</strong> in the browser console or MongoDB
                <pre className="code-block">
{`// Check MongoDB
db.orders.findOne({}, {internalId: 1, "documentAnalysis.aiExtractedFields.PropertyAddress1": 1})

// Or check browser console after upload:
console.log("Order saved:", response.internalId)`}
                </pre>
              </li>
              <li>
                <strong>Copy the UUID</strong> and paste it above
              </li>
            </ol>
          </div>
        </div>

        <div className="setup-section">
          <h2>Test Questions</h2>
          <div className="test-questions">
            <p>Once you start the chat, try asking:</p>
            <ul>
              <li>"What is the property address?"</li>
              <li>"Are there any title issues?"</li>
              <li>"Summarize the loan details"</li>
              <li>"Who are the borrowers?"</li>
              <li>"What is the APN?"</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChatTest;
